import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const AppBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [platform, setPlatform] = useState('android'); // 'ios' or 'android'

  useEffect(() => {
    // Check if user has already dismissed the banner
    const dismissed = localStorage.getItem('appBannerDismissed');
    if (dismissed) return;

    // Only show on mobile devices (screen width < 768px or mobile user agent)
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const isMobileScreen = window.innerWidth < 768;
    const isMobile = isMobileUserAgent || isMobileScreen;
    
    // Don't show on desktop
    if (!isMobile) return;

    // Check if app is running in standalone mode (already installed as PWA)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    if (isStandalone) return;

    // Detect platform (iOS vs Android)
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    setPlatform(isIOS ? 'ios' : 'android');

    // Function to check if cookie consent has been given
    const checkAndShowBanner = () => {
      const cookieConsent = localStorage.getItem('cookieConsent');
      const alreadyDismissed = localStorage.getItem('appBannerDismissed');
      
      if (cookieConsent && !alreadyDismissed) {
        // Small delay after cookie consent for smooth UX
        setTimeout(() => {
          setShowBanner(true);
        }, 800);
        return true;
      }
      return false;
    };

    // If cookie consent already exists, show banner after delay
    if (checkAndShowBanner()) return;

    // Otherwise, poll for cookie consent
    const pollInterval = setInterval(() => {
      if (checkAndShowBanner()) {
        clearInterval(pollInterval);
      }
    }, 500);

    return () => {
      clearInterval(pollInterval);
    };
  }, []);

  const dismissBanner = () => {
    localStorage.setItem('appBannerDismissed', 'true');
    setShowBanner(false);
  };

  const getStoreLink = () => {
    if (platform === 'ios') {
      return 'https://apps.apple.com/au/app/buildpilot/id6477372083';
    }
    return 'https://play.google.com/store/apps/details?id=com.buildpilot.android&hl=en_US';
  };

  const getStoreInfo = () => {
    if (platform === 'ios') {
      return {
        name: 'App Store',
        badge: 'https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83'
      };
    }
    return {
      name: 'Google Play',
      badge: 'https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png'
    };
  };

  if (!showBanner) return null;

  const storeInfo = getStoreInfo();

  return (
    <div 
      className="fixed bottom-4 left-4 right-4 z-50 animate-slide-up"
      data-testid="app-banner"
    >
      <div className="max-w-md mx-auto bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 overflow-hidden">
        <div className="p-3 flex items-center gap-3">
          {/* App Icon */}
          <div className="flex-shrink-0">
            <img 
              src="/buildpilot-app-icon.webp" 
              alt="BuildPilot App"
              className="w-12 h-12 rounded-xl shadow-md"
            />
          </div>
          
          {/* App Info */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 text-sm truncate">
              BuildPilot
            </h3>
            <p className="text-xs text-gray-500 truncate">
              Build your home with confidence
            </p>
            <div className="flex items-center gap-1 mt-0.5">
              <div className="flex text-yellow-400 text-xs">
                ★★★★★
              </div>
              <span className="text-xs text-gray-400">4.9</span>
              <span className="text-xs text-gray-300 mx-1">•</span>
              <span className="text-xs text-gray-400 uppercase">{storeInfo.name}</span>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <a
              href={getStoreLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-full transition-colors"
              data-testid="app-banner-download"
            >
              GET
            </a>
            <button
              onClick={dismissBanner}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Dismiss app banner"
              data-testid="app-banner-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppBanner;
